import * as QueryKeys from "data";
import { Box, Card, Chip } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { ApiKey, Status } from "contexts/api-key-state";
import { useHistory } from "react-router-dom";
import { formatTimestamp } from "utils";
import { getActiveOrganisation, useIdentity } from "contexts/identity-context";
import { resolveIdentityId } from "utils/identity";
import { useQuery } from "react-query";
import { fetchApiKeys } from "data/queries";
import CustomTable from "components/shared/table";
import { generatePath } from "react-router-dom";

const StatusGroup = (props: { status: Status; expireDate: number }) => {
  /**
   * Returns rendered chip(s) of the status of an API key
   */

  const color =
    props.status === "approved"
      ? "success"
      : props.status === "revoked"
      ? "error"
      : "info";

  let expired = false;

  if (props.expireDate !== -1 && props.expireDate <= Math.round(Date.now())) {
    expired = true;
  }

  return (
    <Box
      flexDirection="row-reverse"
      alignItems="center"
      style={{ display: "flex", gap: "5%" }}
    >
      {expired ? (
        <Chip
          label="Expired"
          color="primary"
          style={{ backgroundColor: "red" }}
        />
      ) : (
        <Chip
          label={props.status}
          color={color}
          style={{ textTransform: "uppercase" }}
        />
      )}
    </Box>
  );
};

const keysToRows = (keys: ApiKey[]) => {
  const keys_ = keys.map((key) => {
    return {
      id: key.name,
      createdDate: key.createdDate,
      expireDate: key.expireDate,
      name: key.displayName ? key.displayName : key.name,
      status: key.status,
    };
  });
  return keys_.sort((a, b) => {
    const nameA = a.name.toUpperCase(); // ignore upper and lowercase
    const nameB = b.name.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    // names must be equal
    return 0;
  });
};

const columns: GridColDef[] = [
  { flex: 1, field: "name", headerName: "Name" },
  {
    flex: 1,
    field: "createdDate",
    headerName: "Create Date",

    renderCell: (params: any) => formatTimestamp(params.row.createdDate),
  },
  {
    flex: 1,
    field: "expireDate",
    headerName: "Expiry Date",

    renderCell: (params: any) => formatTimestamp(params.row.expireDate),
  },
  {
    field: "status",
    headerName: "Status",

    disableColumnMenu: true,
    sortable: false,
    editable: false,
    width: 200,

    renderCell: (params: any) => (
      <StatusGroup
        status={params.row.status}
        expireDate={params.row.expireDate}
      />
    ),
  },
];

interface ApiKeyTableProps {
  data: any;
  isLoading: boolean;
  apiKeyDetailsRoute: string;
  accountName?: string;
  accountId?: string;
}

export const ApiKeyTable = (props: ApiKeyTableProps) => {
  const history = useHistory();
  const handleOnRowClick = (params: GridRowParams) => {
    history.push(
      generatePath(props.apiKeyDetailsRoute, {
        accountId: props.accountId,
        keyName: params.id,
      }),
      {
        accountId: props.accountId,
        accountName: props.accountName,
        keyName: params.id,
      }
    );
  };

  return (
    <Card
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <CustomTable
        rows={props.data ? keysToRows(props.data) : []}
        cols={columns}
        handleOnRowClick={handleOnRowClick}
        isLoading={props.isLoading}
        overlayText={"You do not have any API Keys."}
      />
    </Card>
  );
};
